import { useSeoMeta } from '@unhead/react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Bitcoin,
  Zap,
  Coins,
  Radio,
  Sparkles,
  Gamepad2,
  AlertTriangle,
  ExternalLink,
  Moon,
  Sun,
  Link as LinkIcon,
  ChevronDown,
  ChevronUp,
  Brain,
  Heart,
} from 'lucide-react';
import { useAuthor } from '@/hooks/useAuthor';
import { useNotes } from '@/hooks/useNotes';
import { usePictures } from '@/hooks/usePictures';
import { useVideos } from '@/hooks/useVideos';
import { useMusic } from '@/hooks/useMusic';
import { useArticles } from '@/hooks/useArticles';
import { useCustomNIPsList } from '@/hooks/useCustomNIPsList';
import { useMagazines } from '@/hooks/useMagazines';
import { NoteCard } from '@/components/NoteCard';
import { PictureCard } from '@/components/PictureCard';
import { VideoCard } from '@/components/VideoCard';
import { MusicCard } from '@/components/MusicCard';
import { ArticleCard } from '@/components/ArticleCard';
import { CustomNIPCard } from '@/components/CustomNIPCard';
import { MagazineCard } from '@/components/MagazineCard';
import { EbookCard } from '@/components/EbookCard';
import { ProfileCard } from '@/components/ProfileCard';
import { LanguageToggle } from '@/components/LanguageToggle';
import { useTheme } from '@/hooks/useTheme';

const MY_NPUB = 'npub14rg4vrt2v374q95ezeeydu3hkdhmzglcj950mggacap4x0lv0gyq04wun7';
const MY_PUBKEY = 'a8d1560d6a647d501699167246f237b36fb123f89168fda11dc743533fec7a08';

const translations = {
  de: {
    title: 'stl1988 - Für Freiheit & Technologie',
    description: 'Asperger-Autist, Bitcoin-Enthusiast, Freedom Technology Advocate',
    hero: {
      greeting: 'Hallo, ich bin',
      subtitle: 'Freiheit durch Technologie',
    },
    about: {
      title: 'Über mich',
      intro:
        'Ich bin ein Asperger-Autist mit einer Leidenschaft für dezentrale Technologien und digitale Freiheit.',
      passions: {
        title: 'Meine Leidenschaften',
        bitcoin: 'Bitcoin',
        bitcoinDesc:
          'Gesundes Geld für eine freie Zukunft. Das Fundament für finanzielle Souveränität.',
        lightning: 'Lightning Network',
        lightningDesc:
          'Second-Layer-Lösung für Bitcoin. Schnelle und günstige Zahlungen auf der Bitcoin-Blockchain.',
        cashu: 'Cashu',
        cashuDesc: 'Privacy-first eCash Protokoll für Lightning. Echte digitale Privatsphäre.',
        nostr: 'Nostr',
        nostrDesc:
          'Dezentrales Kommunikationsprotokoll. Zensurresistente soziale Netzwerke ohne zentrale Kontrolle.',
        freedom: 'Freedom Technology',
        freedomDesc:
          'Technologien, die Freiheit und Selbstbestimmung fördern, nicht einschränken.',
        ai: 'Generative AI',
        aiDesc:
          'Ich liebe es, mit KI zu experimentieren und kreative Inhalte zu generieren. Kein kommerzieller Nutzen - nur aus Freude am Teilen.',
        gaming: 'Gaming',
        gamingDesc:
          'Nintendo-Fan (Zelda, Mario), Pokémon Hauptspiele, spannende Indie-Games und Minecraft.',
      },
    },
    feeds: {
      title: 'Meine Nostr-Inhalte',
      profile: 'Profil',
      notes: 'Notizen',
      articles: 'Artikel',
      pictures: 'Bilder',
      videos: 'Videos',
      music: 'Musik',
      magazines: 'Magazine',
      ebooks: 'E-Books',
      nips: 'NIPs',
      loading: 'Lade Inhalte...',
      empty: 'Keine Inhalte gefunden.',
      showMore: 'Mehr anzeigen',
      showLess: 'Weniger anzeigen',
    },
    neurodiversity: {
      title: 'Neurodiversität & Asperger-Autismus',
      description: 'Mehr über Autismus, Neurodiversität und wichtige Awareness-Tage',
      link: 'Mehr erfahren',
    },
    links: {
      title: 'Wichtige Links',
      github: 'Meine Link-Sammlung',
      githubDesc: 'Umfassende Sammlung aller wichtigen Links und Ressourcen',
    },
    legacy: {
      title: 'Legacy Social Media',
      warning:
        '⚠️ Warnung: Diese Plattformen sind zentralisiert und können zensieren. Folge mir lieber auf Nostr!',
      description:
        'Ich bin hauptsächlich auf Nostr aktiv. Diese Accounts existieren noch, werden aber seltener genutzt:',
      show: 'Legacy Social Media anzeigen',
      hide: 'Legacy Social Media ausblenden',
    },
    footer: {
      built: 'Vibed with',
      nostr: 'Folge mir auf Nostr',
    },
  },
  en: {
    title: 'stl1988 - For Freedom & Technology',
    description: 'Asperger Autist, Bitcoin Enthusiast, Freedom Technology Advocate',
    hero: {
      greeting: 'Hi, I am',
      subtitle: 'Freedom Through Technology',
    },
    about: {
      title: 'About Me',
      intro:
        'I am an Asperger autist with a passion for decentralized technologies and digital freedom.',
      passions: {
        title: 'My Passions',
        bitcoin: 'Bitcoin',
        bitcoinDesc:
          'Sound money for a free future. The foundation for financial sovereignty.',
        lightning: 'Lightning Network',
        lightningDesc:
          'Second-layer solution for Bitcoin. Fast and cheap payments on the Bitcoin blockchain.',
        cashu: 'Cashu',
        cashuDesc: 'Privacy-first eCash protocol for Lightning. True digital privacy.',
        nostr: 'Nostr',
        nostrDesc:
          'Decentralized communication protocol. Censorship-resistant social networks without central control.',
        freedom: 'Freedom Technology',
        freedomDesc:
          'Technologies that promote freedom and self-determination, not restrict them.',
        ai: 'Generative AI',
        aiDesc:
          'I love experimenting with AI and generating creative content. No commercial use - just for the joy of sharing.',
        gaming: 'Gaming',
        gamingDesc:
          'Nintendo fan (Zelda, Mario), Pokémon main series, interesting indie games, and Minecraft.',
      },
    },
    feeds: {
      title: 'My Nostr Content',
      profile: 'Profile',
      notes: 'Notes',
      articles: 'Articles',
      pictures: 'Pictures',
      videos: 'Videos',
      music: 'Music',
      magazines: 'Magazines',
      ebooks: 'E-Books',
      nips: 'NIPs',
      loading: 'Loading content...',
      empty: 'No content found.',
      showMore: 'Show more',
      showLess: 'Show less',
    },
    neurodiversity: {
      title: 'Neurodiversity & Asperger Autism',
      description: 'Learn more about autism, neurodiversity and important awareness days',
      link: 'Learn more',
    },
    links: {
      title: 'Important Links',
      github: 'My Link Collection',
      githubDesc: 'Comprehensive collection of all important links and resources',
    },
    legacy: {
      title: 'Legacy Social Media',
      warning:
        '⚠️ Warning: These platforms are centralized and can censor. Follow me on Nostr instead!',
      description:
        'I am mainly active on Nostr. These accounts still exist but are rarely used:',
      show: 'Show Legacy Social Media',
      hide: 'Hide Legacy Social Media',
    },
    footer: {
      built: 'Vibed with',
      nostr: 'Follow me on Nostr',
    },
  },
};

const Index = () => {
  const [language, setLanguage] = useState<'de' | 'en'>('de');
  const [showLegacy, setShowLegacy] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'notes' | 'articles' | 'pictures' | 'videos' | 'music' | 'magazines' | 'ebooks' | 'nips'>(
    'profile'
  );
  const { theme, setTheme } = useTheme();

  const t = translations[language];

  const author = useAuthor(MY_PUBKEY);
  const metadata = author.data?.metadata;

  const { data: notes, isLoading: notesLoading } = useNotes(MY_PUBKEY, activeTab === 'notes');
  const { data: articles, isLoading: articlesLoading } = useArticles(MY_PUBKEY, activeTab === 'articles');
  const { data: pictures, isLoading: picturesLoading } = usePictures(MY_PUBKEY, activeTab === 'pictures');
  const { data: videos, isLoading: videosLoading } = useVideos(MY_PUBKEY, activeTab === 'videos');
  const { data: music, isLoading: musicLoading } = useMusic(MY_PUBKEY, activeTab === 'music');
  const { data: customNIPs, isLoading: nipsLoading } = useCustomNIPsList(MY_PUBKEY, activeTab === 'nips');
  const { data: magazinesData, isLoading: magazinesLoading } = useMagazines(MY_PUBKEY, activeTab === 'magazines' || activeTab === 'ebooks');

  // Update meta tags dynamically based on language
  useSeoMeta({
    title: t.title,
    description: t.description,
    ogTitle: t.title,
    ogDescription: t.description,
  });

  const passions = [
    {
      icon: Bitcoin,
      title: t.about.passions.bitcoin,
      description: t.about.passions.bitcoinDesc,
      color: 'text-orange-600 dark:text-orange-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/20 hover:border-orange-500/40',
    },
    {
      icon: Zap,
      title: t.about.passions.lightning,
      description: t.about.passions.lightningDesc,
      color: 'text-yellow-600 dark:text-yellow-500',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/20 hover:border-yellow-500/40',
    },
    {
      icon: Coins,
      title: t.about.passions.cashu,
      description: t.about.passions.cashuDesc,
      color: 'text-gray-700 dark:text-gray-300',
      bgColor: 'bg-gray-500/10',
      borderColor: 'border-gray-500/20 hover:border-gray-500/40',
    },
    {
      icon: Radio,
      title: t.about.passions.nostr,
      description: t.about.passions.nostrDesc,
      color: 'text-purple-600 dark:text-purple-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/20 hover:border-purple-500/40',
    },
    {
      icon: Sparkles,
      title: t.about.passions.ai,
      description: t.about.passions.aiDesc,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20 hover:border-primary/40',
    },
    {
      icon: Gamepad2,
      title: t.about.passions.gaming,
      description: t.about.passions.gamingDesc,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20 hover:border-primary/40',
    },
  ];

  const renderFeedContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileCard pubkey={MY_PUBKEY} npub={MY_NPUB} />;

      case 'notes':
        if (notesLoading) {
          return Array.from({ length: 3 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-4/5" />
                </div>
              </CardContent>
            </Card>
          ));
        }
        if (notes && notes.length > 0) {
          return notes.map((note) => <NoteCard key={note.id} event={note} />);
        }
        break;

      case 'articles':
        if (articlesLoading) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-0">
                    <Skeleton className="h-48 w-full" />
                  </CardContent>
                  <CardHeader>
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                  </CardHeader>
                </Card>
              ))}
            </div>
          );
        }
        if (articles && articles.length > 0) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {articles.map((article) => (
                <ArticleCard key={article.id} event={article} />
              ))}
            </div>
          );
        }
        break;

      case 'pictures':
        if (picturesLoading) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-0">
                    <Skeleton className="h-64 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          );
        }
        if (pictures && pictures.length > 0) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pictures.map((picture) => (
                <PictureCard key={picture.id} event={picture} />
              ))}
            </div>
          );
        }
        break;

      case 'videos':
        if (videosLoading) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-0">
                    <Skeleton className="h-64 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          );
        }
        if (videos && videos.length > 0) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.map((video) => (
                <VideoCard key={video.id} event={video} />
              ))}
            </div>
          );
        }
        break;

      case 'music':
        if (musicLoading) {
          return Array.from({ length: 5 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-16 w-16 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-10 w-10 rounded-full" />
                </div>
              </CardContent>
            </Card>
          ));
        }
        if (music && music.length > 0) {
          return music.map((track) => <MusicCard key={track.id} event={track} />);
        }
        break;

      case 'nips':
        if (nipsLoading) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Card key={i}>
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <Skeleton className="h-10 w-10 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-5 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          );
        }
        if (customNIPs && customNIPs.length > 0) {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {customNIPs.map((nip) => (
                <CustomNIPCard key={nip.id} event={nip} />
              ))}
            </div>
          );
        }
        break;

      case 'magazines': {
        const magSkeleton = (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="w-full aspect-[2/3] rounded-lg" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ))}
          </div>
        );
        if (magazinesLoading) return magSkeleton;
        const magazines = magazinesData?.magazines ?? [];
        const issues = magazinesData?.issues ?? [];
        if (magazines.length > 0) {
          return (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {magazines.map((mag) => {
                const issueCount = issues.filter((iss) =>
                  iss.tags.some(([n, v]) => n === 'a' && v.endsWith(`:${mag.tags.find(([n]) => n === 'd')?.[1]}`))
                ).length;
                return <MagazineCard key={mag.id} magazine={mag} issueCount={issueCount} />;
              })}
            </div>
          );
        }
        break;
      }

      case 'ebooks': {
        const ebookSkeleton = (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="w-full aspect-[2/3] rounded-lg" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ))}
          </div>
        );
        if (magazinesLoading) return ebookSkeleton;
        const ebooks = magazinesData?.ebooks ?? [];
        if (ebooks.length > 0) {
          return (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {ebooks.map((ebook) => (
                <EbookCard key={ebook.id} ebook={ebook} />
              ))}
            </div>
          );
        }
        break;
      }
    }

    return (
      <Card className="border-dashed">
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">{t.feeds.empty}</p>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 sm:h-16 items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Avatar className="h-8 w-8 sm:h-10 sm:w-10 ring-2 ring-primary/20">
              <AvatarImage src={metadata?.picture} alt="stl1988" />
              <AvatarFallback>ST</AvatarFallback>
            </Avatar>
            <div>
              <h1 className="font-heading text-base sm:text-lg font-bold">stl1988</h1>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 sm:h-10 sm:w-10"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            >
              {theme === 'dark' ? (
                <Sun className="h-4 w-4 sm:h-5 sm:w-5" />
              ) : (
                <Moon className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </Button>
            <LanguageToggle
              language={language}
              onToggle={() => setLanguage(language === 'de' ? 'en' : 'de')}
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden border-b">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 animate-pulse" />
        <div className="container relative py-12 sm:py-16 md:py-24 px-4">
          <div className="mx-auto max-w-4xl text-center space-y-6 sm:space-y-8">
            <div className="space-y-3 sm:space-y-4">
              <p className="text-sm sm:text-base md:text-lg text-muted-foreground font-medium">
                {t.hero.greeting}
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                stl1988
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground font-medium">
                {t.hero.subtitle}
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2">
              <Badge variant="secondary" className="px-3 py-1.5 text-xs sm:text-sm">
                <Bitcoin className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 text-orange-600 dark:text-orange-500" />
                Bitcoin
              </Badge>
              <Badge variant="secondary" className="px-3 py-1.5 text-xs sm:text-sm">
                <Zap className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 text-yellow-600 dark:text-yellow-500" />
                Lightning
              </Badge>
              <Badge variant="secondary" className="px-3 py-1.5 text-xs sm:text-sm">
                <Coins className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5" />
                Cashu
              </Badge>
              <Badge variant="secondary" className="px-3 py-1.5 text-xs sm:text-sm">
                <Radio className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 text-purple-600 dark:text-purple-500" />
                Nostr
              </Badge>
              <Badge variant="secondary" className="px-3 py-1.5 text-xs sm:text-sm">
                <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5" />
                AI
              </Badge>
              <Badge variant="secondary" className="px-3 py-1.5 text-xs sm:text-sm">
                <Gamepad2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5" />
                Gaming
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="container py-12 sm:py-16 md:py-24 px-4">
        <div className="mx-auto max-w-6xl space-y-8 sm:space-y-12">
          <div className="text-center space-y-3 sm:space-y-4">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold">
              {t.about.title}
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
              {t.about.intro}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {passions.map((passion, index) => (
              <Card
                key={index}
                className={`group hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 ${passion.borderColor}`}
              >
                <CardHeader className="pb-3">
                  <div
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl ${passion.bgColor} flex items-center justify-center mb-3 sm:mb-4`}
                  >
                    <passion.icon className={`h-5 w-5 sm:h-6 sm:w-6 ${passion.color}`} />
                  </div>
                  <CardTitle className="text-lg sm:text-xl">{passion.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-sm sm:text-base">
                    {passion.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Neurodiversity Section */}
      <section className="container py-12 sm:py-16 border-t px-4">
        <div className="mx-auto max-w-4xl">
          <Card className="border-2 border-blue-500/30 hover:border-blue-500/50 transition-colors bg-gradient-to-br from-blue-50/50 via-purple-50/50 to-pink-50/50 dark:from-blue-950/20 dark:via-purple-950/20 dark:to-pink-950/20">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center">
                  <Brain className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg sm:text-xl">{t.neurodiversity.title}</CardTitle>
                  <CardDescription className="text-sm sm:text-base mt-1">
                    {t.neurodiversity.description}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button asChild variant="default" className="w-full">
                <Link to="/neurodiversity" className="flex items-center justify-between">
                  <span className="font-medium">{t.neurodiversity.link}</span>
                  <Heart className="h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Links Section */}
      <section className="container py-12 sm:py-16 border-t px-4">
        <div className="mx-auto max-w-4xl">
          <Card className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <LinkIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg sm:text-xl">{t.links.title}</CardTitle>
                  <CardDescription className="text-sm sm:text-base mt-1">
                    {t.links.githubDesc}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline" className="w-full">
                <a
                  href="https://stl1988.github.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between"
                >
                  <span className="font-medium">{t.links.github}</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Feeds Section */}
      <section className="container py-12 sm:py-16 md:py-24 border-t px-4">
        <div className="mx-auto max-w-6xl space-y-6 sm:space-y-8">
          <div className="text-center space-y-3 sm:space-y-4">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold">
              {t.feeds.title}
            </h2>
          </div>

          {/* Mobile-friendly tab buttons */}
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={activeTab === 'profile' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('profile')}
              className="text-xs sm:text-sm"
            >
              {t.feeds.profile}
            </Button>
            <Button
              variant={activeTab === 'notes' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('notes')}
              className="text-xs sm:text-sm"
            >
              {t.feeds.notes}
            </Button>
            <Button
              variant={activeTab === 'articles' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('articles')}
              className="text-xs sm:text-sm"
            >
              {t.feeds.articles}
            </Button>
            <Button
              variant={activeTab === 'pictures' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('pictures')}
              className="text-xs sm:text-sm"
            >
              {t.feeds.pictures}
            </Button>
            <Button
              variant={activeTab === 'videos' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('videos')}
              className="text-xs sm:text-sm"
            >
              {t.feeds.videos}
            </Button>
            <Button
              variant={activeTab === 'music' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('music')}
              className="text-xs sm:text-sm"
            >
              {t.feeds.music}
            </Button>
            <Button
              variant={activeTab === 'magazines' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('magazines')}
              className="text-xs sm:text-sm"
            >
              {t.feeds.magazines}
            </Button>
            <Button
              variant={activeTab === 'ebooks' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('ebooks')}
              className="text-xs sm:text-sm"
            >
              {t.feeds.ebooks}
            </Button>
            <Button
              variant={activeTab === 'nips' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab('nips')}
              className="text-xs sm:text-sm"
            >
              {t.feeds.nips}
            </Button>
          </div>

          <div className="space-y-4">{renderFeedContent()}</div>
        </div>
      </section>

      {/* Legacy Social Media Section */}
      <section className="container py-12 sm:py-16 md:py-24 border-t px-4">
        <div className="mx-auto max-w-4xl space-y-6 sm:space-y-8">
          <Button
            variant="outline"
            onClick={() => setShowLegacy(!showLegacy)}
            className="w-full text-sm sm:text-base"
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            {showLegacy ? t.legacy.hide : t.legacy.show}
            {showLegacy ? (
              <ChevronUp className="h-4 w-4 ml-2" />
            ) : (
              <ChevronDown className="h-4 w-4 ml-2" />
            )}
          </Button>

          {showLegacy && (
            <div className="space-y-4 sm:space-y-6">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-sm sm:text-base">
                  {t.legacy.warning}
                </AlertDescription>
              </Alert>

              <Card>
                <CardHeader>
                  <CardDescription className="text-sm sm:text-base">
                    {t.legacy.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <a
                    href="https://x.com/stl1988_neu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <span className="font-medium text-sm sm:text-base">
                      X (Twitter) - New Account
                    </span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <Separator />
                  <a
                    href="https://x.com/stl1988s"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <span className="font-medium text-sm sm:text-base">
                      X (Twitter) - Old Account
                    </span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                  <Separator />
                  <a
                    href="https://www.instagram.com/stl__1988/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-muted transition-colors"
                  >
                    <span className="font-medium text-sm sm:text-base">Instagram</span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8 sm:py-12 bg-muted/30">
        <div className="container px-4">
          <div className="mx-auto max-w-4xl space-y-6 sm:space-y-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-center md:text-left">
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {t.footer.built}{' '}
                  <a
                    href="https://soapbox.pub/mkstack"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary hover:underline"
                  >
                    MKStack
                  </a>
                </p>
              </div>

              <Button asChild variant="default" size="sm" className="text-xs sm:text-sm">
                <a
                  href={`https://njump.me/${MY_NPUB}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Radio className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                  {t.footer.nostr}
                </a>
              </Button>
            </div>

            <Separator />

            <div className="text-center text-xs sm:text-sm text-muted-foreground">
              <p className="break-all">
                npub:{' '}
                <code className="bg-muted px-2 py-1 rounded text-xs">{MY_NPUB}</code>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
